import { Fragment, useCallback, useEffect, useState } from 'react';

import { api, type Voicemail as VoicemailRow } from './api.js';
import {
  ConfirmDialog,
  EmptyState,
  SkeletonTable,
  VoicemailIcon,
  useToast,
} from './ui.js';

function duration(seconds: number | null): string {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Voicemail inbox — every message, since this is the admin view. */
export function Voicemail(): JSX.Element {
  const [messages, setMessages] = useState<VoicemailRow[]>([]);
  const [unread, setUnread] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState<VoicemailRow | null>(null);
  const toast = useToast();

  const load = useCallback((): void => {
    api
      .voicemail(100)
      .then((r) => {
        setMessages(r.voicemails);
        setUnread(r.unread);
      })
      .catch((e) => toast.error(String(e)))
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(load, [load]);

  return (
    <section>
      <div className="section-head">
        <div style={{ display: 'block' }}>
          <h2>
            Voicemail{' '}
            {unread > 0 && <span className="badge bad">{unread} unread</span>}
          </h2>
          <p className="section-sub">
            Messages left when nobody answered. Audio is encrypted at rest and
            decrypted only for playback.
          </p>
        </div>
      </div>

      {loading ? (
        <SkeletonTable rows={4} columns={[22, 16, 16, 20, 10]} />
      ) : messages.length === 0 ? (
        <EmptyState
          icon={<VoicemailIcon size={22} />}
          title="No voicemail"
          body="When a caller leaves a message it appears here, transcribed and ready to play."
        />
      ) : (
        <table>
          <thead>
            <tr>
              <th>From</th><th>To</th><th>For</th>
              <th>Received</th><th>Length</th><th />
            </tr>
          </thead>
          <tbody>
            {messages.map((m) => (
              <Fragment key={m.id}>
                <tr className={m.listenedAt ? '' : 'vm-unread'}>
                  <td>
                    {m.contactName ?? m.fromNumber ?? 'Unknown'}
                    {m.contactName && m.fromNumber && (
                      <span className="perm-label"> {m.fromNumber}</span>
                    )}
                  </td>
                  <td>{m.toLabel ?? m.toNumber ?? '—'}</td>
                  <td>{m.ownerName ?? 'General inbox'}</td>
                  <td>{new Date(m.createdAt).toLocaleString()}</td>
                  <td>{duration(m.durationSec)}</td>
                  <td className="row-actions">
                    <button
                      type="button"
                      className="link"
                      onClick={() => {
                        // Release the previous blob before replacing it.
                        if (audioUrl) URL.revokeObjectURL(audioUrl);
                        setAudioUrl(null);

                        if (openId === m.id) {
                          setOpenId(null);
                          return;
                        }
                        setOpenId(m.id);
                        if (m.hasAudio) {
                          void api
                            .voicemailAudio(m.id)
                            .then(setAudioUrl)
                            .then(() => setTimeout(load, 400))
                            .catch(() => setAudioUrl(null));
                        }
                      }}
                    >
                      {openId === m.id ? 'Close' : 'Open'}
                    </button>
                    <button
                      type="button"
                      className="link"
                      onClick={() =>
                        void api.markVoicemailRead(m.id, !m.listenedAt).then(load)
                      }
                    >
                      Mark {m.listenedAt ? 'unread' : 'read'}
                    </button>
                    <button
                      type="button"
                      className="link danger"
                      onClick={() => setConfirming(m)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>

                {openId === m.id && (
                  <tr className="detail">
                    <td colSpan={6}>
                      {m.hasAudio && audioUrl ? (
                        <audio controls src={audioUrl} />
                      ) : m.hasAudio ? (
                        <p className="hint">Loading audio…</p>
                      ) : (
                        <p className="hint">Audio unavailable.</p>
                      )}

                      {m.transcript ? (
                        <pre>{m.transcript}</pre>
                      ) : (
                        <p className="hint">
                          {m.transcriptStatus === 'queued' ||
                          m.transcriptStatus === 'processing'
                            ? 'Transcription in progress…'
                            : 'No transcript.'}
                        </p>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}

      {confirming && (
        <ConfirmDialog
          title="Delete this voicemail?"
          body="The message and its transcript are removed permanently."
          confirmLabel="Delete"
          destructive
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            void api.deleteVoicemail(confirming.id).then(() => {
              setConfirming(null);
              load();
              toast.push({ title: 'Voicemail deleted', tone: 'ok' });
            });
          }}
        />
      )}
    </section>
  );
}
